import type { DocumentVersionInfo } from "../../services/documentEditingService";

/**
 * Die Fassungen eines Dokuments als Zeitleiste von STÄNDEN (User 07.10.2026).
 *
 * In der Datenbank steht es andersherum: eine `DocumentVersion` ist der Stand,
 * der abgelöst WURDE, und trägt Kommentar, Bearbeiter und Zeitpunkt des
 * Speicherns, das ihn abgelöst hat. So gelesen stand der Kommentar „Unterschrift
 * ergänzt“ neben der Fassung OHNE Unterschrift. Hier wird das je Stand
 * zusammengezogen: Name der Fassung, wie sie vorher hieß, der Kommentar und was
 * geändert wurde — alles zu dem Speichern, aus dem dieser Stand hervorging.
 */
export interface FassungsEintrag {
    key: string;
    /** `null` = der aktuelle Stand des Dokuments. */
    versionNo: number | null;
    titel: string;
    /** Der Stand vor der ersten Bearbeitung. */
    original: boolean;
    /** Dateiname dieses Standes (ohne Ordner). */
    fileName: string;
    /** So hieß die Datei davor — nur gesetzt, wenn dieser Stand umbenannt wurde. */
    vorherName: string | null;
    kommentar: string | null;
    aenderung: string | null;
    /** Wann dieser Stand entstand (ISO) — `null`, wenn unbekannt. */
    wann: string | null;
    wer: string | null;
    fileSize: number | null;
    /** Aus Office (SharePoint Embedded) gesichert — kein Nutzer-Speichern in KMVS. */
    office: { microsoftStand: string | null; original: boolean } | null;
    /** Die Zeile der Historie zu diesem Stand — `null` beim aktuellen. */
    version: DocumentVersionInfo | null;
}

export interface AktuellesDokument {
    fileName: string;
    fileSize?: number | null;
    uploadedAt?: string | null;
}

const SPE_NOTE = "SPE-Fassung dauerhaft gesichert";
const istOffice = (v: DocumentVersionInfo): boolean => !!v.speWorkspaceId || v.note === SPE_NOTE;

/** Bis 07.10.2026 sprang die Beschreibung der Änderung in den Kommentar, wenn der Nutzer keinen schrieb. */
const ALTE_BESCHREIBUNGEN = new Set([
    "PDF bearbeitet",
    "PDF gedreht",
    "Bereiche entfernt",
    "Ausschnitte verschoben",
    "Text geändert",
    "Seiten geändert",
    "Bild bearbeitet",
    "Bild gedreht",
    "Bild in PDF umgewandelt",
    "Bild bearbeitet und in PDF umgewandelt",
]);

/** `Verträge/2019/Foto.jpg` → `Foto.jpg`. */
export const nurDateiname = (name: string | null | undefined): string => {
    const n = String(name ?? "");
    return n.slice(n.lastIndexOf("/") + 1) || n;
};

function deute(v: DocumentVersionInfo): { kommentar: string | null; aenderung: string | null } {
    const note = (v.note ?? "").trim();
    const aenderung = (v.aenderung ?? "").trim() || null;
    const wieder = /^Ersetzt durch Wiederherstellung von Fassung (\d+)$/.exec(note);
    if (wieder) return { kommentar: null, aenderung: `Fassung ${wieder[1]} wiederhergestellt` };
    // Umbenennen über den Namenskasten: dieselbe Datei, nur der Name ist neu —
    // den zeigt die Zeile ohnehin („vorher: …“).
    if (/^Umbenannt in „/.test(note)) return { kommentar: null, aenderung: aenderung ?? "Umbenannt" };
    if (!aenderung && ALTE_BESCHREIBUNGEN.has(note)) return { kommentar: null, aenderung: note };
    return { kommentar: note || null, aenderung };
}

/** Neueste zuerst: der aktuelle Stand, darunter die früheren Fassungen. */
export function baueFassungsListe(versions: readonly DocumentVersionInfo[], doc: AktuellesDokument | null): FassungsEintrag[] {
    const auf = [...versions].sort((a, b) => a.versionNo - b.versionNo);
    const eintraege: FassungsEintrag[] = [];

    auf.forEach((v, i) => {
        const davor = auf[i - 1] ?? null;
        const basis = {
            key: `v${v.id}`,
            versionNo: v.versionNo,
            titel: `Fassung ${v.versionNo}`,
            fileName: nurDateiname(v.fileName),
            vorherName: davor && nurDateiname(davor.fileName) !== nurDateiname(v.fileName) ? nurDateiname(davor.fileName) : null,
            fileSize: v.fileSize ?? null,
            version: v,
        };
        if (istOffice(v)) {
            eintraege.push({
                ...basis,
                original: false,
                kommentar: null,
                aenderung: null,
                wann: v.createdAt,
                wer: null,
                office: {
                    microsoftStand: v.speModifiedAt ?? null,
                    original: !!v.speWorkspaceId && v.speRemoteVersionId == null,
                },
            });
            return;
        }
        if (davor && !istOffice(davor)) {
            eintraege.push({
                ...basis,
                original: false,
                ...deute(davor),
                wann: davor.createdAt,
                wer: davor.replacedByUserName ?? null,
                office: null,
            });
            return;
        }
        eintraege.push({
            ...basis,
            original: !davor,
            kommentar: null,
            aenderung: null,
            wann: !davor ? (doc?.uploadedAt ?? null) : null,
            wer: null,
            office: null,
        });
    });

    if (doc) {
        const letzte = auf[auf.length - 1] ?? null;
        const ausSpeichern = letzte && !istOffice(letzte);
        eintraege.push({
            key: "aktuell",
            versionNo: null,
            titel: "Aktuelle Fassung",
            original: !letzte,
            fileName: nurDateiname(doc.fileName),
            vorherName: letzte && nurDateiname(letzte.fileName) !== nurDateiname(doc.fileName) ? nurDateiname(letzte.fileName) : null,
            ...(ausSpeichern ? deute(letzte) : { kommentar: null, aenderung: letzte ? "Aus Office übernommen" : null }),
            wann: letzte ? letzte.createdAt : (doc.uploadedAt ?? null),
            wer: ausSpeichern ? (letzte.replacedByUserName ?? null) : null,
            fileSize: doc.fileSize ?? null,
            office: null,
            version: null,
        });
    }
    return eintraege.reverse();
}
